import React from 'react';
import { Alert, Box, Paragraph, Spinner, Text, Toast, useToast } from '@zillow/constellation-10';
import { RenterDataSidebar } from '../RenterDataSidebar';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { formatStackedNumbers } from '../../../../utils/formatStackedNumbers';
import { US_REGION_ID, ZG_SHOW_RENTER_DATA_MOCK_SECTIONS } from '../../../../constants';
import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import {
  QuartileCardGrid,
  SectionCard,
  StackedBarList,
  StatCardGrid,
  SummaryMetricCard,
  VerticalBarChart,
} from './components';
import { useRenterData } from './RenterDataReport.data';
import { CustomerAnalyticsPage } from '../../../SharedComponents/CustomerAnalyticsPage';
import type { FileType, SelectedGraphs } from '../../../SharedComponents/ExportDialog';
import { extractRenterData, getRenterDataGraphConfigs } from './RenterDataReport.export';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { downloadExcel } from '../../../../utils/exportUtils';
import { downloadPDF, type TextSection } from '../../../../utils/pdfExportUtils';
import { captureGraphs } from '../../../../utils/captureGraphs';
import { buildCoverPageData } from '../../../../utils/buildCoverPageData';
import { emitRenterDataExportEvent, emitRenterDataExportPdfEvent } from '../../../../analytics/events';
import { getMovingGraphCopy } from './RenterDataReport.utils';

export const RenterDataReport = () => {
  const { filteredProperties, selectedFilters, regionsMapping, selectedMarketId, selectedDisplayingRegionLevel } =
    useMainLayout();
  const { user } = useRentalManagerUser();
  const { enqueueToast } = useToast();
  const showMockDataSections = ZG_SHOW_RENTER_DATA_MOCK_SECTIONS;

  const isNationwide = selectedMarketId === US_REGION_ID;
  const renterData = useRenterData(isNationwide, showMockDataSections);

  const areaName = regionsMapping[selectedMarketId] || (isNationwide && 'nationwide') || 'Selected area';
  const cityName = selectedFilters.cityRegionId ? regionsMapping[selectedFilters.cityRegionId] : undefined;
  const pageTitle = isNationwide
    ? 'Renter activity nationwide for the last 30 days'
    : `Renter activity for ${areaName} for the last 30 days`;

  const movingCopy = getMovingGraphCopy(selectedDisplayingRegionLevel, areaName, isNationwide, cityName);

  const isLoadingAnyApi = false;
  const hasApiError = false;
  const noRegionError =
    filteredProperties.length === 0 ? 'No region data available. Please ensure properties are loaded.' : null;

  const summaryCards = [
    {
      label: 'Median household income',
      value: renterData.medianHouseholdIncome.value,
      delta: renterData.medianHouseholdIncome.delta,
      valueFormatter: formatCurrency,
      deltaFormatter: formatCurrency,
      infoText:
        'Approximate median annual income for households in the selected market, based on aggregations of U.S. Census data.',
    },
    {
      label: 'Population',
      value: renterData.population.value,
      delta: renterData.population.delta,
      valueFormatter: formatStackedNumbers,
      deltaFormatter: formatStackedNumbers,
      infoText:
        'Estimated total number of people living in the selected market, based on aggregations of U.S. Census data.',
    },
    {
      label: 'Rentals searched',
      value: renterData.percentRenters.value,
      delta: renterData.percentRenters.delta,
      valueFormatter: (val: number) => `${val}%`,
      deltaFormatter: (val: number) => `${val}%`,
      infoText: 'Share of Zillow users who conducted a rental search in the selected market.',
    },
  ];

  const graphConfigs = getRenterDataGraphConfigs();

  const handleExport = async (fileType: FileType, selectedGraphs: SelectedGraphs) => {
    if (user) {
      const emitMap = { xls: emitRenterDataExportEvent, pdf: emitRenterDataExportPdfEvent } as const;
      emitMap[fileType]?.({ user });
    }

    try {
      switch (fileType) {
        case 'xls': {
          const graphsData = extractRenterData(renterData, selectedGraphs);
          if (graphsData.length === 0) {
            enqueueToast(
              <Toast tone="critical">
                <Text>No data to export.</Text>
              </Toast>,
            );
            return;
          }
          await downloadExcel(graphsData, 'Renter_Data');
          break;
        }
        case 'pdf': {
          const graphImages = await captureGraphs('Renter Data', selectedGraphs);
          const tableData = extractRenterData(renterData, selectedGraphs);

          const pdfGraphPages = graphImages.map((graph) => {
            const normalizedGraphName = graph.name.toLowerCase().replace(/^total\s+/, '');
            const matchingTable = tableData.find((td) => {
              const normalizedTableName = td.name
                .toLowerCase()
                .replace(/_/g, ' ')
                .replace(/\s+(multifamily|single family)$/, '');
              return (
                normalizedTableName === normalizedGraphName ||
                normalizedTableName.startsWith(normalizedGraphName) ||
                normalizedGraphName.startsWith(normalizedTableName)
              );
            });
            return {
              name: graph.name,
              imageData: graph.imageData,
              tableData: matchingTable,
              legend: graph.legend,
              description: graph.description,
            };
          });

          const textSections: Array<TextSection> = [];
          if (selectedGraphs['summary-cards']) {
            textSections.push({
              title: 'Summary Metrics',
              items: [
                {
                  label: 'Median household income',
                  value:
                    renterData.medianHouseholdIncome.value !== null
                      ? `$${renterData.medianHouseholdIncome.value.toLocaleString()}`
                      : '—',
                  change:
                    renterData.medianHouseholdIncome.value !== null
                      ? `$${renterData.medianHouseholdIncome.delta.toLocaleString()}`
                      : '',
                },
                {
                  label: 'Population',
                  value: renterData.population.value !== null ? renterData.population.value.toLocaleString() : '—',
                  change: renterData.population.value !== null ? renterData.population.delta.toLocaleString() : '',
                },
                {
                  label: 'Percentage who rent',
                  value: renterData.percentRenters.value !== null ? `${renterData.percentRenters.value}%` : '—',
                  change: renterData.percentRenters.value !== null ? `${renterData.percentRenters.delta}%` : '',
                },
              ],
            });
          }
          if (selectedGraphs['pet-ownership']) {
            textSections.push({
              title: 'Pets Searched',
              items: renterData.petOwnership.map((item) => ({
                label: item.label,
                value: `${item.value}%`,
              })),
            });
          }
          if (selectedGraphs['renters-moved-from']) {
            textSections.push({
              title: 'Top 10 Cities Renters Moved From',
              items: renterData.rentersMovedFrom.map((item) => ({
                label: item.label,
                value: `${item.value}%`,
              })),
            });
          }
          if (selectedGraphs['renters-moved-to']) {
            textSections.push({
              title: 'Top 10 Cities Renters Moved To',
              items: renterData.rentersMovedTo.map((item) => ({
                label: item.label,
                value: `${item.value}%`,
              })),
            });
          }
          if (selectedGraphs['affordable-rent-ranges'] && renterData.rentQuartiles) {
            textSections.push({
              title: 'Affordable Rent Ranges',
              items: renterData.rentQuartiles.map((item) => ({
                label: item.label,
                value: item.value,
              })),
            });
          }
          if (selectedGraphs['annual-income']) {
            textSections.push({
              title: 'Annual Renter Income',
              items: renterData.annualIncome.map((item) => ({
                label: item.label,
                value: `${item.value}%`,
              })),
            });
          }
          if (selectedGraphs['top-amenity-searches']) {
            textSections.push({
              title: 'Top 5 Amenity Searches',
              items: renterData.topAmenitySearches.map((item) => ({
                label: item.label,
                value: `${item.value}%`,
              })),
            });
          }
          if (selectedGraphs['top-keyword-searches']) {
            textSections.push({
              title: 'Top 5 Keyword Searches',
              items: renterData.topKeywordSearches.map((item) => ({
                label: item.label,
                value: `${item.value}%`,
              })),
            });
          }

          if (pdfGraphPages.length === 0 && textSections.length === 0) {
            enqueueToast(
              <Toast tone="critical">
                <Text>No data to export.</Text>
              </Toast>,
            );
            return;
          }

          const coverPageData = await buildCoverPageData(
            'Renter data',
            selectedFilters,
            regionsMapping,
            filteredProperties,
          );
          await downloadPDF(pdfGraphPages, 'Renter Data', textSections, coverPageData);
          break;
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to export data.';
      enqueueToast(
        <Toast tone="critical">
          <Text>{errorMessage}</Text>
        </Toast>,
      );
      throw error;
    }
  };

  const renderContent = () => {
    const isLoading = isLoadingAnyApi;
    const combinedError = noRegionError || (hasApiError && 'Unable to load some renter data. Please try again later.');

    if (isLoading) {
      return (
        <Box css={{ display: 'flex', justifyContent: 'center', padding: '800' }}>
          <Spinner size="lg" />
        </Box>
      );
    }

    if (combinedError) {
      return (
        <Box css={{ padding: '600' }}>
          <Paragraph css={{ color: 'text.error' }}>{combinedError}</Paragraph>
        </Box>
      );
    }

    return (
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '600' }}>
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: { base: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: '400',
          }}
          data-graph-id="summary-cards"
        >
          {summaryCards.map((card) => (
            <SummaryMetricCard
              key={card.label}
              label={card.label}
              value={card.value}
              delta={card.delta}
              valueFormatter={card.valueFormatter}
              deltaFormatter={card.deltaFormatter}
              infoText={card.infoText}
              isLoading={renterData.isLoadingDemographics}
            />
          ))}
        </Box>

        <Box
          css={{ display: 'grid', gridTemplateColumns: { base: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: '600' }}
        >
          <SectionCard
            title="Pets searched"
            data-graph-id="pet-ownership"
            tooltip="Share of users who conducted a search with the pets filter applied."
            loading={renterData.isLoadingPets}
            compact
          >
            {renterData.petOwnership.length > 0 ? (
              <StatCardGrid data={renterData.petOwnership} showIcons />
            ) : (
              <Alert tone="info">No data available</Alert>
            )}
          </SectionCard>

          <SectionCard
            title="Affordable rent ranges"
            data-graph-id="affordable-rent-ranges"
            tooltip="Estimated affordable rent ranges based on Zillow renter search activity in the selected market."
            loading={renterData.isLoadingAffordableRent}
            compact
          >
            {renterData.rentQuartiles ? (
              <QuartileCardGrid data={renterData.rentQuartiles} />
            ) : (
              <Alert tone="info">No data available</Alert>
            )}
          </SectionCard>

          <SectionCard
            title="Bedrooms searched"
            data-graph-id="bedrooms-searched"
            tooltip="Share of users who conducted a search with each bedroom filter applied."
            loading={renterData.isLoadingBedrooms}
          >
            {renterData.bedroomsSearched.length > 0 ? (
              <VerticalBarChart data={renterData.bedroomsSearched} />
            ) : (
              <Alert tone="info">No data available</Alert>
            )}
          </SectionCard>

          <SectionCard
            title="Bathrooms searched"
            data-graph-id="bathrooms-searched"
            tooltip="Share of users who conducted a search with each bathroom filter applied."
            loading={renterData.isLoadingBathrooms}
          >
            {renterData.bathroomsSearched.length > 0 ? (
              <VerticalBarChart data={renterData.bathroomsSearched} />
            ) : (
              <Alert tone="info">No data available</Alert>
            )}
          </SectionCard>

          <SectionCard
            title={movingCopy.searchingFromTitle}
            tooltip={movingCopy.searchingFromTooltip}
            data-graph-id="renters-moved-from"
            loading={renterData.isLoadingCityMoving}
          >
            {renterData.rentersMovedFrom.length > 0 ? (
              <StackedBarList data={renterData.rentersMovedFrom} />
            ) : (
              <Alert tone="info">No data available</Alert>
            )}
          </SectionCard>

          <SectionCard
            title={movingCopy.searchingForTitle}
            tooltip={movingCopy.searchingForTooltip}
            data-graph-id="renters-moved-to"
            loading={renterData.isLoadingCityMoving}
          >
            {renterData.rentersMovedTo.length > 0 ? (
              <StackedBarList data={renterData.rentersMovedTo} />
            ) : (
              <Alert tone="info">No data available</Alert>
            )}
          </SectionCard>

          <SectionCard
            title="Top amenity searches"
            data-graph-id="top-amenity-searches"
            tooltip="Share of users who conducted a search with each amenity filter applied."
            loading={renterData.isLoadingAmenities}
          >
            {renterData.topAmenitySearches.length > 0 ? (
              <StackedBarList data={renterData.topAmenitySearches} />
            ) : (
              <Alert tone="info">No data available</Alert>
            )}
          </SectionCard>

          <SectionCard
            title="Top keyword searches"
            data-graph-id="top-keyword-searches"
            tooltip="Share of users who conducted a search with each keyword filter applied."
            loading={renterData.isLoadingKeywords}
          >
            {renterData.topKeywordSearches.length > 0 ? (
              <StackedBarList data={renterData.topKeywordSearches} />
            ) : (
              <Alert tone="info">No data available</Alert>
            )}
          </SectionCard>

          <SectionCard
            title="Annual renter income"
            data-graph-id="annual-income"
            loading={renterData.isLoadingExpectations}
          >
            {renterData.annualIncome.length > 0 ? (
              <StackedBarList data={renterData.annualIncome} />
            ) : (
              <Alert tone="info">No data available</Alert>
            )}
          </SectionCard>
        </Box>
      </Box>
    );
  };

  return (
    <CustomerAnalyticsPage
      header={pageTitle}
      subheader="Source: Zillow internal data and census data"
      sidebarTitle="Renter data"
      sidebar={<RenterDataSidebar areaName={areaName} isNationwide={isNationwide} />}
      tabName="Renter Data"
      graphConfigs={graphConfigs}
      onExport={handleExport}
      marketSelectLabel="Market"
    >
      {renderContent()}
    </CustomerAnalyticsPage>
  );
};
